import React, { useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { ExternalLink, X } from 'lucide-react';
import DesignerInvoiceReviewPanel from './DesignerInvoiceReviewPanel';
import { db } from '../../utils/firebase';
import { qboVendorBillAppUrl } from '../../utils/purchaseQbBillStatus';
import {
  subscribeApprovedDesignerInvoices,
  type DesignerInvoiceDoc,
} from '../../utils/designerInvoiceService';
import type { QBExpense, QBInvoice } from '../../types';
import './DesignerInvoiceReviewPanel.css';
import './BillingsHubModal.css';

interface BillingsHubModalProps {
  open: boolean;
  onClose: () => void;
}

type HubQbRow = {
  id: string;
  kind: 'bill' | 'invoice';
  party: string;
  date: string;
  amount: number;
  status: string;
  qbUrl: string;
  docLabel: string;
};

const MONEY = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function qboInvoiceAppUrl(invoiceId: string): string {
  const id = invoiceId.trim();
  return id ? `https://app.qbo.intuit.com/app/invoice?txnId=${encodeURIComponent(id)}` : '';
}

function expenseTiedToProject(expense: QBExpense): boolean {
  if (String(expense.matchedDealId || '').trim()) return true;
  const splits = Array.isArray(expense.splits) ? expense.splits : [];
  return splits.some((s) => String(s?.dealId || '').trim());
}

function invoiceTiedToProject(invoice: QBInvoice): boolean {
  return Boolean(String(invoice.matchedDealId || '').trim());
}

function billStatusLabel(expense: QBExpense): string {
  const balance = Number(expense.balance);
  const total = Number(expense.totalAmount) || 0;
  if (Number.isFinite(balance)) {
    if (balance <= 0.009 && total > 0) return 'paid';
    if (balance > 0 && balance < total - 0.009) return 'partial';
    if (balance > 0) return 'open';
  }
  return expense.category === 'overhead' ? 'overhead' : expense.category || 'open';
}

function formatTxnDate(raw: string | undefined): string {
  if (!raw) return '—';
  const iso = String(raw).slice(0, 10);
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso || '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Non-project QB bills/invoices from Firestore mirrors, plus designer invoice review. */
const BillingsHubModal: React.FC<BillingsHubModalProps> = ({ open, onClose }) => {
  const [bills, setBills] = useState<Array<QBExpense & { id: string }>>([]);
  const [invoices, setInvoices] = useState<Array<QBInvoice & { id: string }>>([]);
  const [designerInvoices, setDesignerInvoices] = useState<DesignerInvoiceDoc[]>([]);
  const [listenError, setListenError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    setListenError(null);
    const billsQuery = query(
      collection(db, 'qbExpenses'),
      orderBy('txnDate', 'desc'),
      limit(1000),
    );
    const unsubBills = onSnapshot(
      billsQuery,
      (snap) => {
        setBills(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as QBExpense) })),
        );
      },
      (err) => setListenError(err.message || 'Could not load QuickBooks bills'),
    );
    const unsubInvoices = onSnapshot(
      collection(db, 'qbInvoices'),
      (snap) => {
        setInvoices(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as QBInvoice) })),
        );
      },
      (err) => setListenError(err.message || 'Could not load QuickBooks invoices'),
    );
    const unsubDesigner = subscribeApprovedDesignerInvoices((list) => {
      setDesignerInvoices(list);
    });
    return () => {
      unsubBills();
      unsubInvoices();
      unsubDesigner();
    };
  }, [open]);

  const openDesignerBills = useMemo(() => {
    const byQbId = new Map<string, QBExpense & { id: string }>();
    for (const bill of bills) {
      if (bill.entityType !== 'bill') continue;
      const qbId = String(bill.quickbooksId || '').trim();
      if (qbId) byQbId.set(qbId, bill);
      byQbId.set(bill.id, bill);
    }
    const out: Array<{
      id: string;
      vendor: string;
      jobLabel: string;
      amount: number;
      balance: number;
      qbUrl: string;
      docLabel: string;
      invoiceNumber: string;
    }> = [];
    for (const inv of designerInvoices) {
      if (inv.status !== 'approved') continue;
      const qbId = String(inv.qboBillId || inv.qbExpenseId || '').trim();
      if (!qbId) continue;
      const qb = byQbId.get(qbId);
      const balance = qb && Number.isFinite(Number(qb.balance))
        ? Number(qb.balance)
        : Number(inv.invoiceTotal) || 0;
      if (balance <= 0.009) continue;
      out.push({
        id: inv.id,
        vendor: inv.vendor || 'Designer',
        jobLabel: inv.forLabel || inv.suggestedJobLabel || '—',
        amount: Number(inv.invoiceTotal) || 0,
        balance,
        qbUrl: qboVendorBillAppUrl(String(qb?.quickbooksId || qbId)),
        docLabel: String(qb?.docNumber || qb?.quickbooksId || qbId),
        invoiceNumber: inv.invoiceNumber || '',
      });
    }
    out.sort((a, b) => b.balance - a.balance || a.vendor.localeCompare(b.vendor));
    return out;
  }, [bills, designerInvoices]);

  const rows = useMemo(() => {
    const out: HubQbRow[] = [];
    for (const bill of bills) {
      if (bill.entityType !== 'bill') continue;
      if (bill.excluded) continue;
      if (expenseTiedToProject(bill)) continue;
      const qbId = String(bill.quickbooksId || '').trim();
      out.push({
        id: `bill-${bill.id}`,
        kind: 'bill',
        party: bill.vendorName || 'Vendor',
        date: String(bill.txnDate || '').slice(0, 10),
        amount: Number(bill.totalAmount) || 0,
        status: billStatusLabel(bill),
        qbUrl: qboVendorBillAppUrl(qbId),
        docLabel: bill.docNumber || qbId || bill.id,
      });
    }
    for (const invoice of invoices) {
      if (invoiceTiedToProject(invoice)) continue;
      if (invoice.status === 'voided' || invoice.status === 'removed_in_qb') continue;
      const qbId = String(invoice.quickbooksInvoiceId || '').trim();
      out.push({
        id: `inv-${invoice.id}`,
        kind: 'invoice',
        party: invoice.customerName || 'Customer',
        date: String(invoice.txnDate || '').slice(0, 10),
        amount: Number(invoice.totalAmount) || 0,
        status: invoice.status || 'open',
        qbUrl: qboInvoiceAppUrl(qbId),
        docLabel: invoice.invoiceNumber || qbId || invoice.id || '',
      });
    }
    out.sort((a, b) => String(b.date).localeCompare(String(a.date)));
    return out;
  }, [bills, invoices]);

  if (!open) return null;

  return (
    <div className="sa-ppl-modal-backdrop dir-modal-backdrop" onClick={onClose}>
      <div
        className="sa-ppl-modal dir-modal billings-hub-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="billings-hub-title"
      >
        <div className="sa-ppl-modal-head">
          <div>
            <h3 id="billings-hub-title">Billings Hub</h3>
            <p className="sa-ppl-modal-sub">
              QuickBooks bills and invoices that are not tied to a project, plus designer
              invoice review. Under-contract payables stay on Billing &amp; Payables.
            </p>
          </div>
          <button type="button" className="sa-ppl-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="sa-ppl-modal-body dir-modal-body billings-hub-body">
          <div className="billings-hub-review">
            <DesignerInvoiceReviewPanel compact onQueueEmpty={() => undefined} />
          </div>
          <section className="billings-hub-records" aria-labelledby="billings-hub-open-designer-title">
            <div className="billings-hub-records-head">
              <h4 id="billings-hub-open-designer-title">Open designer bills</h4>
              <p className="billings-hub-records-sub">
                Approved designer invoices with an open QuickBooks balance.
              </p>
            </div>
            {openDesignerBills.length === 0 ? (
              <p className="billings-hub-empty">No open designer QuickBooks balances right now.</p>
            ) : (
              <div className="billings-hub-table-wrap">
                <table className="billings-hub-table">
                  <thead>
                    <tr>
                      <th scope="col">Vendor</th>
                      <th scope="col">Job</th>
                      <th scope="col">Invoice</th>
                      <th scope="col">Balance</th>
                      <th scope="col">QuickBooks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openDesignerBills.map((row) => (
                      <tr key={row.id}>
                        <td>{row.vendor}</td>
                        <td>{row.jobLabel}</td>
                        <td>{row.invoiceNumber || '—'}</td>
                        <td className="billings-hub-amount">{MONEY(row.balance)}</td>
                        <td>
                          {row.qbUrl ? (
                            <a
                              className="billings-hub-qb-link"
                              href={row.qbUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {row.docLabel || 'Open'}
                              <ExternalLink size={12} aria-hidden />
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <section className="billings-hub-records" aria-labelledby="billings-hub-records-title">
            <div className="billings-hub-records-head">
              <h4 id="billings-hub-records-title">Non-project QuickBooks records</h4>
              <p className="billings-hub-records-sub">
                Synced bills and invoices with no deal/project link (historical and current).
              </p>
            </div>
            {listenError && (
              <p className="dir-status dir-status--error" role="alert">{listenError}</p>
            )}
            {rows.length === 0 && !listenError ? (
              <p className="billings-hub-empty">No untied QuickBooks bills or invoices right now.</p>
            ) : (
              <div className="billings-hub-table-wrap">
                <table className="billings-hub-table">
                  <thead>
                    <tr>
                      <th scope="col">Type</th>
                      <th scope="col">Vendor / Customer</th>
                      <th scope="col">Date</th>
                      <th scope="col">Amount</th>
                      <th scope="col">Status</th>
                      <th scope="col">QuickBooks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className={`billings-hub-kind billings-hub-kind--${row.kind}`}>
                            {row.kind === 'bill' ? 'Bill' : 'Invoice'}
                          </span>
                        </td>
                        <td>{row.party}</td>
                        <td>{formatTxnDate(row.date)}</td>
                        <td className="billings-hub-amount">{MONEY(row.amount)}</td>
                        <td className="billings-hub-status">{row.status}</td>
                        <td>
                          {row.qbUrl ? (
                            <a
                              className="billings-hub-qb-link"
                              href={row.qbUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {row.docLabel || 'Open'}
                              <ExternalLink size={12} aria-hidden />
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default BillingsHubModal;
